// Phase 25 iter-9 — BillingPage: vista de facturación dedicada que reusa
// os endpoints xa existentes (/billing/subscription + /billing/portal-session).
// Para detalles de facturas redirixe ao Stripe Customer Portal.

import { CreditCard, ExternalLink, FileText, Sparkles } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, Navigate } from 'react-router-dom';

import type { SubscriptionResponse } from '@leyrochain/shared';

import { getSubscription, openCustomerPortal } from '../api/billing';
import { describeAxiosError } from '../api/errors';
import ContentPage from '../components/ui/ContentPage';
import Spinner from '../components/ui/Spinner';
import { useAuthContext } from '../context/AuthContext';
import { useToast } from '../context/ToastContext';

export default function BillingPage(): JSX.Element {
  const { t } = useTranslation();
  const { accessToken } = useAuthContext();
  const { addToast } = useToast();
  const [sub, setSub] = useState<SubscriptionResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [opening, setOpening] = useState(false);

  useEffect(() => {
    if (!accessToken) return;
    getSubscription()
      .then(setSub)
      .catch(() =>
        setSub({ plan: 'FREE', has_subscription: false, renews_at: null, trial_days: 0 }),
      )
      .finally(() => setLoading(false));
  }, [accessToken]);

  if (!accessToken) {
    return <Navigate to="/marketplace/login?next=/marketplace/facturacion" replace />;
  }

  async function handlePortal() {
    setOpening(true);
    try {
      const { url } = await openCustomerPortal();
      window.location.assign(url);
    } catch (err) {
      addToast(describeAxiosError(err, t('common.error')), 'error');
      setOpening(false);
    }
  }

  const planLabel = sub
    ? sub.plan === 'FREE'
      ? t('billing.free')
      : sub.plan === 'FARMER_BASIC'
        ? t('billing.basic')
        : t('billing.pro')
    : '—';

  return (
    <ContentPage title={t('billing.title')} intro={t('billing.subtitle')} category="Conta">
      {loading && (
        <div className="flex items-center gap-2 text-sm text-muted">
          <Spinner size={16} /> {t('common.loading')}
        </div>
      )}

      {sub && (
        <>
          <section className="not-prose border border-border rounded-2xl p-6 bg-white">
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 rounded-2xl bg-rauxa/10 text-rauxa flex items-center justify-center shrink-0">
                {sub.plan === 'FREE' ? <Sparkles size={22} /> : <CreditCard size={22} />}
              </div>
              <div className="flex-1">
                <p className="text-xs uppercase tracking-wide text-muted font-medium">
                  {t('billing.currentPlan')}
                </p>
                <p className="text-2xl font-bold text-dark mt-0.5">{planLabel}</p>
                {sub.renews_at && (
                  <p className="text-sm text-muted mt-2">
                    {t('billing.nextRenewal', {
                      date: new Date(sub.renews_at).toLocaleDateString('gl-ES'),
                    })}
                  </p>
                )}
              </div>
            </div>

            {sub.has_subscription ? (
              <div className="mt-5 flex flex-col sm:flex-row gap-3 items-start sm:items-center">
                <button
                  type="button"
                  onClick={() => void handlePortal()}
                  disabled={opening}
                  className="btn-primary inline-flex items-center gap-2 disabled:opacity-60"
                >
                  {opening ? (
                    <Spinner size={14} />
                  ) : (
                    <>
                      {t('billing.managePortal')}
                      <ExternalLink size={14} />
                    </>
                  )}
                </button>
                <p className="text-xs text-muted max-w-md">{t('billing.portalDesc')}</p>
              </div>
            ) : (
              <div className="mt-5 flex flex-col sm:flex-row gap-3 items-start sm:items-center">
                <Link
                  to="/marketplace/plans"
                  className="btn-primary inline-flex items-center gap-2"
                >
                  {t('billing.seePlans')}
                </Link>
                <p className="text-xs text-muted max-w-md">{t('billing.notSubscribed')}</p>
              </div>
            )}
          </section>

          <section className="not-prose border border-border rounded-2xl p-6 bg-white mt-4">
            <div className="flex items-start gap-3">
              <FileText size={18} className="text-muted shrink-0 mt-0.5" />
              <p className="text-sm text-muted leading-relaxed">{t('billing.invoiceNote')}</p>
            </div>
          </section>
        </>
      )}
    </ContentPage>
  );
}
